/** /tag-category-sets — named tag-category bundles applied to a TO (grid + CRUD dialog). */
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, Chip, IconButton, Stack, Tooltip } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import { list as listSets, remove as removeSet, type TagCategorySet } from '@/api/tagCategorySets';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { PageHeader } from '@/components/PageHeader';
import { useSnackbar } from '@/components/Snackbar';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { PAGE_FILL_SX } from '@/layout/pageFill';

import { TagCategorySetDialog } from './TagCategorySetDialog';

export function TagCategorySetsListPage() {
  const { hasPerm } = usePermissions();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const canManage = hasPerm('console_preflight_manage');

  const [editing, setEditing] = useState<TagCategorySet | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toDelete, setToDelete] = useState<TagCategorySet | null>(null);

  useCreateIntent(() => openCreate());

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = useCallback((s: TagCategorySet) => {
    setEditing(s);
    setDialogOpen(true);
  }, []);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => removeSet(id),
    onSuccess: () => {
      snackbar.show('Tag category set deleted.', 'success');
      qc.invalidateQueries({ queryKey: ['tag_category_sets'] });
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error'),
  });

  const columns = useMemo<GridColDef<TagCategorySet>[]>(
    () => [
      copyableColumn({ field: 'code', headerName: 'Code', width: 180 }),
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
      {
        field: 'restech',
        headerName: 'Restech',
        width: 140,
        sortable: false,
        renderCell: (p) =>
          p.row.restech ? <Chip size="small" variant="outlined" label={p.row.restech} /> : '—',
      },
      {
        field: 'items',
        headerName: 'Categories',
        width: 120,
        sortable: false,
        valueGetter: (_, row) => row.items.length,
      },
      ...(canManage
        ? [
            {
              field: 'actions',
              headerName: '',
              width: 96,
              sortable: false,
              filterable: false,
              renderCell: (p: { row: TagCategorySet }) => (
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(p.row);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        setToDelete(p.row);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ),
            } as GridColDef<TagCategorySet>,
          ]
        : []),
    ],
    [canManage, openEdit],
  );

  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      listSets({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? 'code',
      }),
    [],
  );

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="preflight-concept"
        title="Tag Category Sets"
        subtitle="Named bundles of tag-category definitions applied to a tour operator."
        actions={
          canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New set
            </Button>
          )
        }
      />

      <TagCategorySetDialog open={dialogOpen} set={editing} onClose={() => setDialogOpen(false)} />

      <ResourceDataGrid<TagCategorySet>
        queryKey={['tag_category_sets']}
        queryFn={queryFn}
        columns={columns}
        defaultSort={{ field: 'code', sort: 'asc' }}
        emptyMessage="No tag category sets yet."
        emptyAction={
          canManage ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New set
            </Button>
          ) : undefined
        }
        headerToolbar={{
          exportFileName: 'tag-category-sets',
        }}
      />

      <ConfirmDialog
        open={toDelete !== null}
        title="Delete this set?"
        message={toDelete ? `"${toDelete.name}" will be deleted. This cannot be undone.` : ''}
        confirmLabel="Delete set"
        destructive
        onConfirm={() => {
          if (toDelete) deleteMutation.mutate(toDelete.id);
          setToDelete(null);
        }}
        onCancel={() => setToDelete(null)}
      />
    </Box>
  );
}
